import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '../../../../utils/api';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarIcon, SparklesIcon, ExclamationTriangleIcon, CheckCircleIcon, ListBulletIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export const Route = createFileRoute('/people/schedulers/generate-schedule')({
    component: GenerateSchedulePage,
})

// Helper functions for date manipulation
function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff));
}

function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function formatDateYMD(date: Date): string {
    return date.toISOString().split('T')[0];
}

function formatDateDisplay(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
    });
}

function formatWeekDisplay(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

interface ScheduleAssignment {
    shift_id: string;
    employee_id: string;
    employee_name: string;
    scheduler_id: string;
    scheduler_name: string;
    job_name?: string;
    shift_title: string;
    shift_date: string;
    shift_start: string;
    shift_end: string;
}

interface BlockedCandidate {
    employee_id: string;
    employee_name: string;
    blocking_reason: string;
}

interface UnfilledShift {
    shift_id: string;
    shift_date: string;
    shift_start: string;
    shift_title: string;
    scheduler_id: string;
    scheduler_name: string;
    job_name?: string;
    required_job_id?: string | null;
    reason: 'no_eligible_employees' | 'constraint_conflict';
    blocked_candidates: BlockedCandidate[];
}

interface ScheduleGenerationResponse {
    assignments: ScheduleAssignment[];
    unfilled_shifts: UnfilledShift[];
    warnings: string[];
    total_shifts: number;
    filled_count: number;
    unfilled_count: number;
}

function GenerateSchedulePage() {
    const { t } = useI18n();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ScheduleGenerationResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedWeekStart, setSelectedWeekStart] = useState<string>(
        formatDateYMD(getMonday(new Date()))
    );
    const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
    const [selectedSchedulerId, setSelectedSchedulerId] = useState<string>('all');

    const handleGenerateSchedule = async () => {
        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await apiFetch(
                '/api/timesheet/schedulers/generate-schedule',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        week_start_date: selectedWeekStart,
                        timeout_seconds: 30,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
                throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            setResult(data);
        } catch (err: any) {
            setError(err.message || 'Failed to generate schedule');
        } finally {
            setLoading(false);
        }
    };

    // Get all dates in the week
    const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = addDays(new Date(selectedWeekStart), i);
        return formatDateYMD(date);
    });

    // Group by Date -> Location -> Shifts (Mixed Filled/Unfilled)
    const scheduleByDateAndLocation = weekDates.reduce((acc, date) => {
        acc[date] = {};
        return acc;
    }, {} as Record<string, Record<string, (ScheduleAssignment | UnfilledShift)[]>>);

    if (result) {
        // Process filled assignments
        result.assignments.forEach(assignment => {
            const date = assignment.shift_date;
            const location = assignment.scheduler_name;
            if (scheduleByDateAndLocation[date]) {
                if (!scheduleByDateAndLocation[date][location]) {
                    scheduleByDateAndLocation[date][location] = [];
                }
                scheduleByDateAndLocation[date][location].push(assignment);
            }
        });

        // Process unfilled shifts
        result.unfilled_shifts.forEach(shift => {
            const date = shift.shift_date;
            const location = shift.scheduler_name;
            if (scheduleByDateAndLocation[date]) {
                if (!scheduleByDateAndLocation[date][location]) {
                    scheduleByDateAndLocation[date][location] = [];
                }
                scheduleByDateAndLocation[date][location].push(shift);
            }
        });

        // Sort each location's shifts by time
        Object.keys(scheduleByDateAndLocation).forEach(date => {
            Object.keys(scheduleByDateAndLocation[date]).forEach(location => {
                scheduleByDateAndLocation[date][location].sort((a, b) => {
                    const timeA = 'shift_start' in a ? a.shift_start : (a as ScheduleAssignment).shift_start; // Both have shift_start, but TS might need help
                    const timeB = 'shift_start' in b ? b.shift_start : (b as ScheduleAssignment).shift_start;
                    return timeA.localeCompare(timeB);
                });
            });
        });
    }

    // --- Calendar View Logic ---

    // Get unique schedulers for dropdown
    const schedulers = result ? Array.from(new Set([
        ...result.assignments.map(a => JSON.stringify({ id: a.scheduler_id, name: a.scheduler_name })),
        ...result.unfilled_shifts.map(u => JSON.stringify({ id: u.scheduler_id, name: u.scheduler_name }))
    ])).map(s => JSON.parse(s) as { id: string, name: string }) : [];

    // Filter data for selected scheduler
    const filteredAssignments = result?.assignments.filter(a => selectedSchedulerId === 'all' || a.scheduler_id === selectedSchedulerId) || [];
    const filteredUnfilled = result?.unfilled_shifts.filter(u => selectedSchedulerId === 'all' || u.scheduler_id === selectedSchedulerId) || [];

    // Group by Employee -> Date for Grid
    // We need a list of all employees involved in the filtered results
    const employeeMap = new Map<string, string>(); // id -> name
    filteredAssignments.forEach(a => employeeMap.set(a.employee_id, a.employee_name));

    // Also include "Unfilled" as a pseudo-employee row if there are unfilled shifts
    const hasUnfilled = filteredUnfilled.length > 0;

    const gridEmployees = Array.from(employeeMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));

    // Helper to check if a shift overlaps a day (simplified for daily grid)
    // Since we have shift_date, we can just compare dates
    function getShiftsForCell(employeeId: string, date: string) {
        return filteredAssignments.filter(a => a.employee_id === employeeId && a.shift_date === date);
    }

    function getUnfilledForCell(date: string) {
        return filteredUnfilled.filter(u => u.shift_date === date);
    }

    // Helper for contrast text color (reused from $id.tsx logic, simplified default)
    function getContrastTextColor(bg?: string | null): string {
        return '#111827'; // Default dark text for now as we don't have colors in response yet
    }


    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <SparklesIcon className="h-8 w-8" />
                            Generate Schedule
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Automatically assign employees to shifts using AI-powered scheduling
                        </p>
                    </div>
                </div>

                {/* Week Selector and Generate Button */}
                <Card>
                    <CardHeader>
                        <CardTitle>Select Week</CardTitle>
                        <CardDescription>Choose the Monday of the week you want to schedule</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex gap-4 items-end">
                            <div className="flex-1">
                                <label className="text-sm font-medium mb-2 block">Week Starting (Monday)</label>
                                <input
                                    type="date"
                                    value={selectedWeekStart}
                                    onChange={(e) => setSelectedWeekStart(e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                            </div>
                            <Button onClick={handleGenerateSchedule} disabled={loading} size="lg">
                                {loading ? (
                                    <>
                                        <Skeleton className="h-4 w-4 mr-2" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <SparklesIcon className="h-4 w-4 mr-2" />
                                        Generate Schedule
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Error Display */}
                {error && (
                    <Alert variant="destructive">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Results */}
                {result && (
                    <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'calendar')} className="space-y-6">
                        {/* Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Shifts</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{result.total_shifts}</div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Filled</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-green-600 flex items-center gap-2">
                                        <CheckCircleIcon className="h-6 w-6" />
                                        {result.filled_count}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Unfilled</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-orange-600 flex items-center gap-2">
                                        <ExclamationTriangleIcon className="h-6 w-6" />
                                        {result.unfilled_count}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Warnings */}
                        {result.warnings.length > 0 && (
                            <Alert>
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                <AlertDescription>
                                    <ul className="list-disc list-inside">
                                        {result.warnings.map((warning, idx) => (
                                            <li key={idx}>{warning}</li>
                                        ))}
                                    </ul>
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <h2 className="text-xl font-semibold">Proposed Schedule</h2>
                                <Badge variant="outline" className="text-sm">
                                    {result.filled_count} / {result.total_shifts} shifts filled
                                </Badge>
                            </div>
                            <div className="flex items-center gap-4">
                                {viewMode === 'calendar' && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">Scheduler:</span>
                                        <Select value={selectedSchedulerId} onValueChange={setSelectedSchedulerId}>
                                            <SelectTrigger className="w-[200px]">
                                                <SelectValue placeholder="Select scheduler" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Schedulers</SelectItem>
                                                {schedulers.map(s => (
                                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {/* View Toggle */}
                                <TabsList>
                                    <TabsTrigger value="list" className="gap-2">
                                        <ListBulletIcon className="h-4 w-4" />
                                        List
                                    </TabsTrigger>
                                    <TabsTrigger value="calendar" className="gap-2">
                                        <TableCellsIcon className="h-4 w-4" />
                                        Calendar
                                    </TabsTrigger>
                                </TabsList>
                            </div>
                        </div>

                        <TabsContent value="list" className="mt-0">
                            <Card>
                                <CardContent className="p-0">
                                    {weekDates.map((date) => {
                                        const locations = scheduleByDateAndLocation[date] || {};
                                        const locationNames = Object.keys(locations).sort();

                                        if (locationNames.length === 0) return null;

                                        return (
                                            <div key={date} className="border-b pb-4 last:border-b-0 p-6">
                                                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                                    <CalendarIcon className="h-5 w-5" />
                                                    {formatDateDisplay(date)}
                                                </h3>

                                                <div className="space-y-4 pl-4">
                                                    {locationNames.map(location => {
                                                        const shifts = locations[location];
                                                        return (
                                                            <div key={location} className="bg-muted/30 rounded-lg p-4">
                                                                <h4 className="font-medium text-md mb-3 flex items-center gap-2">
                                                                    <Badge variant="outline">{location}</Badge>
                                                                </h4>

                                                                <Table>
                                                                    <TableHeader>
                                                                        <TableRow className="w-full">
                                                                            <TableHead className="w-24 w-min-20 w-max-24">Time</TableHead>
                                                                            <TableHead className="w-24 w-min-20 w-max-24">Shift</TableHead>
                                                                            <TableHead className="w-30 w-min-30 w-max-60">Job</TableHead>
                                                                            <TableHead className="w-48 w-min-48 w-max-60">Employee / Status</TableHead>
                                                                        </TableRow>
                                                                    </TableHeader>
                                                                    <TableBody>
                                                                        {shifts.map((item) => {
                                                                            const isUnfilled = 'reason' in item;
                                                                            const shift = item as (ScheduleAssignment & UnfilledShift); // Intersection for common fields access

                                                                            return (
                                                                                <TableRow key={shift.shift_id}>
                                                                                    <TableCell className="font-mono text-sm">
                                                                                        {shift.shift_start.substring(0, 5)}
                                                                                    </TableCell>
                                                                                    <TableCell>{shift.shift_title}</TableCell>
                                                                                    <TableCell>
                                                                                        {shift.job_name && (
                                                                                            <Badge variant="outline" className="font-normal">
                                                                                                {shift.job_name}
                                                                                            </Badge>
                                                                                        )}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {isUnfilled ? (
                                                                                            <div className="flex flex-col gap-1">
                                                                                                <Badge variant="destructive" className="w-fit">
                                                                                                    {shift.reason === 'no_eligible_employees'
                                                                                                        ? 'No eligible employees'
                                                                                                        : 'Constraint conflict'}
                                                                                                </Badge>
                                                                                                {shift.blocked_candidates && shift.blocked_candidates.length > 0 && (
                                                                                                    <span className="text-xs text-muted-foreground">
                                                                                                        Blocked: {shift.blocked_candidates.map(c => c.employee_name).join(', ')}
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        ) : (
                                                                                            <Badge variant="secondary">{(item as ScheduleAssignment).employee_name}</Badge>
                                                                                        )}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            );
                                                                        })}
                                                                    </TableBody>
                                                                </Table>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="calendar" className="mt-0">
                            <Card>
                                <CardContent className="p-0 overflow-x-auto">
                                    <div className="min-w-[900px]">
                                        {/* Header row */}
                                        <div className="grid" style={{ gridTemplateColumns: `200px repeat(7, 1fr)` }}>
                                            <div className="sticky left-0 bg-background z-10 py-2 font-medium border-b px-4">Employee</div>
                                            {weekDates.map((date) => (
                                                <div key={date} className="py-2 text-center font-medium border-l border-b px-2">
                                                    {formatDateDisplay(date).split(',')[0]} <br />
                                                    <span className="text-xs text-muted-foreground">{date.split('-').slice(1).join('/')}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Unfilled Shifts Row */}
                                        {hasUnfilled && (
                                            <div className="grid items-stretch border-b bg-red-50/30" style={{ gridTemplateColumns: `200px repeat(7, 1fr)` }}>
                                                <div className="sticky left-0 bg-background/95 z-10 p-4 font-medium text-destructive flex items-center border-r">
                                                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                                                    Unfilled Shifts
                                                </div>
                                                {weekDates.map((date) => {
                                                    const shifts = getUnfilledForCell(date);
                                                    return (
                                                        <div key={date} className="p-2 border-l min-h-[60px]">
                                                            <div className="flex flex-col gap-1">
                                                                {shifts.map(shift => (
                                                                    <Tooltip key={shift.shift_id}>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="text-xs rounded-md px-2 py-1 bg-red-100 text-red-800 border border-red-200 cursor-default">
                                                                                <div className="font-semibold">{shift.shift_start.substring(0, 5)} - {shift.shift_title}</div>
                                                                                {shift.job_name && <div>{shift.job_name}</div>}
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <div className="text-sm">
                                                                                <p className="font-bold text-red-600 mb-1">
                                                                                    {shift.reason === 'no_eligible_employees' ? 'No eligible employees' : 'Constraint conflict'}
                                                                                </p>
                                                                                {shift.blocked_candidates.length > 0 && (
                                                                                    <div>
                                                                                        <p className="font-semibold">Blocked candidates:</p>
                                                                                        <ul className="list-disc pl-4">
                                                                                            {shift.blocked_candidates.map(c => (
                                                                                                <li key={c.employee_id}>{c.employee_name}: {c.blocking_reason}</li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Employee Rows */}
                                        {gridEmployees.map(emp => (
                                            <div key={emp.id} className="grid items-stretch border-b hover:bg-muted/10" style={{ gridTemplateColumns: `200px repeat(7, 1fr)` }}>
                                                <div className="sticky left-0 bg-background z-10 p-4 font-medium border-r flex items-center">
                                                    {emp.name}
                                                </div>
                                                {weekDates.map((date) => {
                                                    const shifts = getShiftsForCell(emp.id, date);
                                                    return (
                                                        <div key={date} className="p-2 border-l min-h-[60px]">
                                                            <div className="flex flex-col gap-1">
                                                                {shifts.map(shift => (
                                                                    <Tooltip key={shift.shift_id}>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="text-xs rounded-md px-2 py-1 bg-blue-100 text-blue-800 border border-blue-200 cursor-default">
                                                                                <div className="font-semibold">{shift.shift_start.substring(0, 5)} - {shift.shift_title}</div>
                                                                                {shift.job_name && <div>{shift.job_name}</div>}
                                                                                <div className="text-[10px] text-blue-600 truncate">{shift.scheduler_name}</div>
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p><strong>Shift:</strong> {shift.shift_title}</p>
                                                                            <p><strong>Time:</strong> {shift.shift_start} - {shift.shift_end}</p>
                                                                            <p><strong>Location:</strong> {shift.scheduler_name}</p>
                                                                            {shift.job_name && <p><strong>Job:</strong> {shift.job_name}</p>}
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}

                                        {gridEmployees.length === 0 && !hasUnfilled && (
                                            <div className="p-8 text-center text-muted-foreground">
                                                No shifts found for the selected filter.
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                )}
            </div>
        </Layout>
    );
}

export default GenerateSchedulePage;
